import * as React from 'react';

import {IconAvatar} from 'shells/avatar/icon-avatar';
import {colors} from 'shells/colors/colors';
import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {getFileIcon} from 'shells/icon/get-icon';
import {DownloadOutlineIcon} from 'shells/icon/svg/download-outline-icon';
import {EyeRegularIcon} from 'shells/icon/svg/eye-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {BaseRow} from 'shells/row/base-row/base-row';

import {AttachmentRowReadState} from './attachment-row-read-state';

type Props = {
    name: string;
    fileId: string;
    fileType: string;
    href?: string;
    viewHref?: string;
    viewableInBrowser: boolean;
    uploadedTime: number | null | undefined;
    userName: string | null | undefined;
    userAvatarUrl: string | null | undefined;
    userInitials: string | null | undefined;
    onRemove: (fileId: string) => void;
};

export function AttachmentRow(props: Props) {
    const {onRemove} = props;

    const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = React.useState<boolean>(false);
    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const toolbarOptions = React.useMemo(
        () => [
            ...(props.viewHref && props.viewableInBrowser
                ? [
                      {
                          id: 'view',
                          icon: EyeRegularIcon,
                          tooltipText: 'View',
                          onClick: () => {
                              window.open(props.viewHref, '_blank');
                          },
                      },
                  ]
                : []),
            ...(props.href
                ? [
                      {
                          id: 'download',
                          icon: DownloadOutlineIcon,
                          tooltipText: 'Download',
                          onClick: () => {
                              window.open(props.href, '_blank');
                          },
                      },
                  ]
                : []),
            {
                id: 'remove',
                icon: TrashRegularIcon,
                tooltipText: 'Remove',
                onClick: () => {
                    setIsRemoveConfirmationOpen(true);
                },
                // @ts-expect-error
                getRef: (ref) => {
                    removeButtonRef.current = ref;
                },
            },
        ],
        [props.viewHref, props.viewableInBrowser, props.href]
    );

    const handleRowClick = () => {
        if (props.viewableInBrowser && props.viewHref) {
            window.open(props.viewHref, '_blank');
        } else if (props.href) {
            window.open(props.href, '_blank');
        }
    };

    const onConfirmRemoveAttachment = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        onRemove(props.fileId);
    }, [props.fileId, onRemove]);

    const onCancelRemoveAttachment = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
    }, []);

    const fileIcon = getFileIcon(props.fileType);

    return (
        <>
            <BaseRow
                type='attachments'
                title={props.name}
                customIcon={
                    <IconAvatar
                        icon={fileIcon}
                        avatarSize={32}
                        iconSize={16}
                        iconColor={colors.person}
                        backgroundColor={colors.blueXlt}
                        shape='square'
                    />
                }
                onClick={handleRowClick}
                toolbarProps={{
                    options: toolbarOptions,
                    forceShowToolbar: isRemoveConfirmationOpen,
                }}
            >
                <AttachmentRowReadState
                    userName={props.userName}
                    userAvatarUrl={props.userAvatarUrl}
                    userInitials={props.userInitials}
                    uploadedTime={props.uploadedTime}
                />
            </BaseRow>
            {isRemoveConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={removeButtonRef.current}
                    type='attachment'
                    parentEntity='lead'
                    onConfirm={onConfirmRemoveAttachment}
                    onCancel={onCancelRemoveAttachment}
                />
            )}
        </>
    );
}
